/**
 * Requirements Editor Component
 * Displays protocol modification requirements as an editable list
 *
 * Simplified format (Dec 4, 2025):
 * - id: REQ-001, REQ-002, etc.
 * - content: Full text description
 *
 * Features:
 * - Edit existing requirements
 * - Delete requirements
 * - Add new requirements
 * - Auto-save to backend API
 */

import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Chip,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  DescriptionOutlined as DescriptionIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { Notification } from '@signanthealth/modular-ui';
import {apiService} from '../services/api';
import { useWizardNavigation } from '../contexts/WizardNavigationContext';

interface Requirement {
  id: string;
  content: string;
}

interface RequirementsEditorProps {
  requirementsData: any; // Full response from API
  onRequirementsChanged?: () => void; // Optional callback when requirements are saved
  onRegenerate?: () => void; // Callback to regenerate requirements
  onViewComparison?: () => void; // Callback to view PRS comparison
  hasComparison?: boolean; // Whether comparison data exists
}

export const RequirementsEditor: React.FC<RequirementsEditorProps> = ({
  requirementsData,
  onRequirementsChanged,
  onRegenerate,
  onViewComparison,
  hasComparison,
}) => {
  const { navigationState, clearTarget } = useWizardNavigation();
  const requirementRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  // Local state for requirements (allows editing before save)
  const [localRequirements, setLocalRequirements] = useState<Requirement[] | null>(null);

  // Edit dialog state
  const [selectedRequirement, setSelectedRequirement] = useState<Requirement | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedContent, setEditedContent] = useState('');

  // Add dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newContent, setNewContent] = useState('');

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requirementToDelete, setRequirementToDelete] = useState<Requirement | null>(null);

  // Saving state
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{open: boolean; message: string; severity: 'success' | 'error'}>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Parse requirements from JSON array format
  const parsedRequirements = useMemo(() => {
    if (!requirementsData?.requirements) {
      console.warn('❌ No requirementsData.requirements');
      return [];
    }

    let rawRequirements = requirementsData.requirements;

    // Handle double-nesting: requirements.requirements (base PRS format)
    if (rawRequirements && typeof rawRequirements === 'object' && rawRequirements.requirements) {
      rawRequirements = rawRequirements.requirements;
    }

    // Validate array format
    if (!Array.isArray(rawRequirements)) {
      console.error('❌ Requirements is not an array:', rawRequirements);
      return [];
    }

    // Map to simplified internal format (just id and content)
    return rawRequirements.map((req: any, index: number) => ({
      id: req.id || `REQ-${String(index + 1).padStart(3, '0')}`,
      content: req.content || 'Untitled',
    }));
  }, [requirementsData]);

  // Reset local state when source data changes from parent
  // This ensures we pick up fresh data after parent re-fetches
  useEffect(() => {
    setLocalRequirements(null);
  }, [requirementsData]);

  // Use local requirements if modified, otherwise use parsed
  const requirements = localRequirements ?? parsedRequirements;

  // Auto-focus effect for navigation from unresolved actions
  useEffect(() => {
    const { targetRequirementId, focusOptions } = navigationState;

    if (targetRequirementId && requirements.length > 0) {
      // Small delay to ensure DOM is rendered
      const focusTimer = setTimeout(() => {
        const targetElement = requirementRefs.current[targetRequirementId];

        if (targetElement) {
          // Scroll to requirement
          if (focusOptions.autoScroll) {
            targetElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }

          // Highlight requirement
          if (focusOptions.highlight) {
            targetElement.style.transition = 'background-color 0.3s ease, border 0.3s ease';
            targetElement.style.backgroundColor = '#e3f2fd'; // Light blue
            targetElement.style.border = '2px solid #2196f3'; // Blue border

            // Remove highlight after 3 seconds
            const highlightTimer = setTimeout(() => {
              targetElement.style.backgroundColor = '';
              targetElement.style.border = '';
            }, 3000);

            return () => clearTimeout(highlightTimer);
          }

          // Show success message
          if (focusOptions.showMessage) {
            setSnackbar({
              open: true,
              message: `Navigated to ${targetRequirementId} from unresolved action`,
              severity: 'success',
            });
          }

          // Clear navigation target after processing
          clearTarget();
        } else {
          // Requirement not found - show warning
          console.warn(`Requirement ${targetRequirementId} not found in current list`);
          setSnackbar({
            open: true,
            message: `Requirement ${targetRequirementId} not found. Please check if it exists.`,
            severity: 'error',
          });
          clearTarget();
        }
      }, 100);

      return () => clearTimeout(focusTimer);
    }
  }, [navigationState, requirements, clearTarget, setSnackbar]);

  // Get session info from requirementsData
  const trialId = requirementsData?.trial_id;
  const sessionId = requirementsData?.session_id;
  const platform = requirementsData?.platform || 'app';

  // Save requirements to backend
  const saveRequirements = useCallback(async (reqs: Requirement[]): Promise<boolean> => {
    if (!trialId || !sessionId) {
      console.error('Missing trialId or sessionId for saving');
      setSnackbar({open: true, message: 'Cannot save: missing session info', severity: 'error'});
      return false;
    }

    setSaving(true);
    console.log('💾 Saving requirements:', reqs);

    try {
      const result = await apiService.saveRequirements(trialId, sessionId, platform, reqs, true);
      console.log('💾 Save result:', result);

      if (result.success) {
        // Update local state immediately for responsive UI
        setLocalRequirements([...reqs]);
        console.log('✅ Local state updated with:', reqs);

        setSnackbar({open: true, message: 'Requirements saved successfully', severity: 'success'});

        // Notify parent (optional - if parent re-fetches, useEffect will reset localRequirements)
        onRequirementsChanged?.();
        return true;
      } else {
        setSnackbar({open: true, message: result.message || 'Failed to save', severity: 'error'});
        return false;
      }
    } catch (error) {
      console.error('Error saving requirements:', error);
      setSnackbar({open: true, message: 'Error saving requirements', severity: 'error'});
      return false;
    } finally {
      setSaving(false);
    }
  }, [trialId, sessionId, platform, onRequirementsChanged]);

  // Generate next requirement ID
  const getNextId = useCallback(() => {
    const maxNum = requirements.reduce((max, req) => {
      const match = req.id.match(/REQ-(\d+)/);
      return match ? Math.max(max, parseInt(match[1], 10)) : max;
    }, 0);
    return `REQ-${String(maxNum + 1).padStart(3, '0')}`;
  }, [requirements]);

  // Edit handlers
  const handleEdit = (req: Requirement) => {
    setSelectedRequirement(req);
    setEditedContent(req.content);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedRequirement(null);
    setEditedContent('');
  };

  const handleSave = async () => {
    if (!selectedRequirement || !editedContent.trim()) return;

    // Create updated requirements array with edited content
    const updatedReqs = requirements.map(req =>
      req.id === selectedRequirement.id
        ? {id: req.id, content: editedContent.trim()}  // Create fresh object
        : {id: req.id, content: req.content}  // Create fresh object for immutability
    );

    console.log('📝 Editing requirement:', selectedRequirement.id);
    console.log('📝 New content:', editedContent.trim());

    const success = await saveRequirements(updatedReqs);
    if (success) {
      handleCloseEditDialog();
    }
  };

  // Delete handlers
  const handleDeleteClick = (req: Requirement) => {
    setRequirementToDelete(req);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setRequirementToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!requirementToDelete) return;

    console.log('🗑️ Deleting requirement:', requirementToDelete.id);

    // Create fresh array without the deleted requirement
    const updatedReqs = requirements
      .filter(req => req.id !== requirementToDelete.id)
      .map(req => ({id: req.id, content: req.content}));

    const success = await saveRequirements(updatedReqs);
    if (success) {
      handleCloseDeleteDialog();
    }
  };

  // Add handlers
  const handleAddClick = () => {
    setNewContent('');
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setNewContent('');
  };

  const handleAddSave = async () => {
    if (!newContent.trim()) return;

    const newReq: Requirement = {
      id: getNextId(),
      content: newContent.trim(),
    };

    console.log('➕ Adding new requirement:', newReq.id);

    // Create fresh array with new requirement
    const updatedReqs = [
      ...requirements.map(req => ({id: req.id, content: req.content})),
      newReq,
    ];

    const success = await saveRequirements(updatedReqs);
    if (success) {
      handleCloseAddDialog();
    }
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({...prev, open: false}));
  };

  // When there is no requirements data at all (e.g. user hasn't run generation), show generate CTA only.
  const hasSessionInfo = requirementsData && (requirementsData.trial_id != null && requirementsData.session_id != null);
  if (!requirementsData || !hasSessionInfo) {
    return (
      <Box sx={{
        py: 8,
        px: 3,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <DescriptionIcon sx={{fontSize: 64, color: 'grey.400', mb: 2}} />
        <Typography sx={{fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 1}}>
          No Requirements Generated
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{mb: 3, maxWidth: 400, textAlign: 'center'}}
        >
          Generate requirements from the PRS comparison to see what needs to be changed in the protocol.
        </Typography>
        {onRegenerate && (
          <Button
            variant="contained"
            startIcon={<PlayArrowIcon />}
            onClick={onRegenerate}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1.5,
            }}
          >
            Generate Requirements
          </Button>
        )}
      </Box>
    );
  }

  const noChangesDetected =
    requirements.length === 0 &&
    (requirementsData?._uiNoChangesDetected === true ||
      requirementsData?.skipped_generation === true ||
      requirementsData?.skip_reason === 'no_changes_detected');

  return (
    <Box>
      {/* Header with action buttons (matching Flutter layout) */}
      <Box sx={{display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 2}}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          disabled={saving}
          sx={{
            textTransform: 'none',
            color: 'success.main',
            borderColor: 'success.main',
            '&:hover': {borderColor: 'success.dark', bgcolor: 'rgba(46,125,50,0.04)'},
          }}
        >
          NEW
        </Button>
        {hasComparison && onViewComparison && (
          <Button
            variant="outlined"
            size="small"
            onClick={onViewComparison}
            sx={{textTransform: 'none'}}
          >
            VIEW COMPARISON
          </Button>
        )}
        {onRegenerate && (
          <Tooltip title="Regenerate requirements from PRS comparison">
            <Button
              variant="contained"
              size="small"
              onClick={onRegenerate}
              sx={{textTransform: 'none'}}
            >
              RE-GENERATE REQUIREMENTS
            </Button>
          </Tooltip>
        )}
      </Box>

      {/* Empty state message when no requirements were generated */}
      {requirements.length === 0 && (
        <Box
          sx={{
            py: 3,
            px: 2,
            mb: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'action.hover',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <DescriptionIcon sx={{fontSize: 40, color: 'grey.400'}} />
          <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center'}}>
            {noChangesDetected
              ? 'The source and target PRS have no meaningful differences for this platform, so no requirements were created. You can add your own below.'
              : 'No requirements were generated. Add your own using the NEW button above.'}
          </Typography>
        </Box>
      )}

      {/* Requirements List */}
      <Box sx={{border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden'}}>
        {requirements.map((req, index) => (
          <Box
            key={req.id}
            ref={(el) => {
              requirementRefs.current[req.id] = el as HTMLElement | null;
            }}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 2,
              p: 2,
              borderBottom: index < requirements.length - 1 ? 1 : 'none',
              borderColor: 'divider',
              transition: 'background-color 0.15s',
              '&:hover': {bgcolor: 'action.hover'},
            }}
          >
            {/* Left: Requirement ID badge (Flutter style) */}
            <Box
              sx={{
                px: 1.25,
                py: '5px',
                borderRadius: 1,
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,119,139,0.15)' : 'rgba(0,119,139,0.08)',
                border: '1px solid',
                borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,119,139,0.4)' : 'rgba(0,119,139,0.25)',
                flexShrink: 0,
                minWidth: 64,
                textAlign: 'center',
              }}
            >
              <Typography sx={{fontWeight: 600, color: 'primary.main', whiteSpace: 'nowrap', fontSize: '0.75rem', lineHeight: 1}}>
                {req.id}
              </Typography>
            </Box>

            {/* Middle: Content */}
            <Box sx={{flex: 1}}>
              <Typography component="span" sx={{fontWeight: 600, mr: 0.75, fontSize: '0.875rem', lineHeight: 1.5}}>
                {index + 1}.
              </Typography>
              <Typography component="span" sx={{fontSize: '0.875rem', color: 'text.primary', lineHeight: 1.5}}>
                {req.content}
              </Typography>
            </Box>

            {/* Right: Edit and Delete buttons */}
            <Box sx={{display: 'flex', gap: 0.5, flexShrink: 0}}>
              <Tooltip title="Edit requirement">
                <IconButton
                  size="small"
                  onClick={() => handleEdit(req)}
                  sx={{color: 'primary.main'}}
                  disabled={saving}
                >
                  <EditIcon sx={{fontSize: 20}} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete requirement">
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(req)}
                  sx={{color: 'error.main'}}
                  disabled={saving}
                >
                  <DeleteIcon sx={{fontSize: 20}} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Metadata footer */}
      <Typography variant="caption" color="text.disabled" sx={{mt: 1.5, display: 'block'}}>
        {requirements.length} requirement{requirements.length !== 1 ? 's' : ''}
      </Typography>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Typography variant="h6">
                Edit Requirement
              </Typography>
              {selectedRequirement && (
                <Chip
                  label={selectedRequirement.id}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{fontWeight: 600}}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseEditDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            label="Requirement Content"
            multiline
            rows={6}
            fullWidth
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            variant="outlined"
            placeholder="Edit the requirement content..."
            disabled={saving}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editedContent.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
              <Typography variant="h6">
                Add New Requirement
              </Typography>
              <Chip
                label={getNextId()}
                size="small"
                color="success"
                variant="outlined"
                sx={{fontWeight: 600}}
              />
            </Box>
            <IconButton onClick={handleCloseAddDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            label="Requirement Content"
            multiline
            rows={6}
            fullWidth
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            variant="outlined"
            placeholder="Enter the requirement description..."
            disabled={saving}
            autoFocus
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleAddSave}
            disabled={saving || !newContent.trim()}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Adding...' : 'Add Requirement'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
      >
        <DialogTitle>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <DeleteIcon color="error" />
            <Typography variant="h6">Delete Requirement?</Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          {requirementToDelete && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Are you sure you want to delete this requirement?
              </Typography>
              <Paper variant="outlined" sx={{p: 2, mt: 2, bgcolor: 'warning.light'}}>
                <Chip
                  label={requirementToDelete.id}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{fontWeight: 600, mb: 1}}
                />
                <Typography variant="body2">
                  {requirementToDelete.content}
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {saving ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification for feedback */}
      <Notification
        snackbarProps={{
          open: snackbar.open,
          autoHideDuration: 4000,
          onClose: handleCloseSnackbar,
          anchorOrigin: {vertical: 'top', horizontal: 'center'},
        }}
        alertProps={{
          onClose: handleCloseSnackbar,
          severity: snackbar.severity,
        }}
      >
        {snackbar.message}
      </Notification>
    </Box>
  );
};
